// Utility functions for CTM Chat Widget

export function generateTabId() {
  // Generate a unique ID for this tab instance
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768 && window.innerHeight <= 1024) ||
         ('ontouchstart' in window) ||
         (navigator.maxTouchPoints > 0);
}

// Cookie handling functions
export function getCookie(name) {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.startsWith(name + "=")) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
}

export function setCookie(name, value, options = {}) {
  const { days, minutes } = options;
  let expires = "";
  if (days) {
    expires = `; expires=${new Date(Date.now() + days * 864e5).toUTCString()}`;
  } else if (minutes) {
    expires = `; expires=${new Date(Date.now() + minutes * 60000).toUTCString()}`;
  }
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value || ""}${expires}; path=/; SameSite=Lax${secure}`;
}

export function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}


export function getSessionIdFromCookie(cookieName) {
  return getCookie(cookieName);
}

export function setSessionIdCookie(cookieName, id, days = 7) {
  if (!id) return;
  setCookie(cookieName, id, { days });
}

export function deleteSessionIdCookie(cookieName) {
  deleteCookie(cookieName);
}

// Tab-specific storage functions
export function getTabReconnectKey(chatId, tabId) {
  // Key for storing tab-specific reconnect attempts
  return `ctm_chat_reconnect_${chatId}_${tabId}`;
}

export function getTabReconnectAttempts(chatId, tabId) {
  try {
    const stored = sessionStorage.getItem(getTabReconnectKey(chatId, tabId));
    if (stored) {
      const data = JSON.parse(stored);
      // Reset if last attempt was more than 5 minutes ago
      if (Date.now() - data.timestamp > 300000) {
        return 0;
      }
      return data.attempts || 0;
    }
  } catch (e) {
    // Ignore sessionStorage errors
  }
  return 0;
}

export function setTabReconnectAttempts(chatId, tabId, attempts) {
  try {
    sessionStorage.setItem(
      getTabReconnectKey(chatId, tabId),
      JSON.stringify({
        attempts: attempts,
        timestamp: Date.now()
      })
    );
  } catch (e) {
    // Ignore sessionStorage errors (e.g., quota exceeded)
  }
}

// Debounce utility
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Format time utility
export function formatTime(date) {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes < 10 ? '0' + minutes : minutes;
  return `${displayHours}:${displayMinutes} ${ampm}`;
}

// Generate unique ID
export function generateId() {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Check if element is in viewport
export function isElementInViewport(el) {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

// Sanitize text for HTML display
export function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Fix ordered list numbering when markdown parsers split a single
// numbered list into multiple <ol> elements (e.g. when <ul> bullets
// appear between numbered items).
export function fixOrderedListNumbering(html) {
  if (!html) return html;
  const temp = document.createElement('div');
  temp.innerHTML = html;

  let runningCount = 0;
  let inSequence = false;

  for (const child of temp.children) {
    if (child.tagName === 'OL') {
      if (inSequence) {
        child.setAttribute('start', String(runningCount + 1));
      }
      runningCount += child.querySelectorAll(':scope > li').length;
      inSequence = true;
    } else if (child.tagName === 'UL') {
      // Unordered lists between ordered lists don't break the sequence
    } else {
      inSequence = false;
      runningCount = 0;
    }
  }

  return temp.innerHTML;
}
